import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import cond from 'lodash/cond';
import Logger from 'observability/Logger';

import { getEventId } from 'common/store/events/selectors';
import { getEventRsvpMethod, getCurrentTicketId } from 'common/entities/event/selectors';
import { REGISTRATION_METHOD_TICKETS, REGISTRATION_METHOD_RSVP } from 'common/constants/registrationMethods';
import { getIsUserUsingRoleBasedPermissions } from 'common/store/userProperties/selectors';

import { SuiContainer } from '@splashthat/sui';
import { withFlags } from 'utils/react-context';
import CubeLoaderOverlay from 'common/components/CubeLoaderOverlay';
import { FULLSCREEN_SIZE } from 'common/components/CubeLoader';
import { colorAppBackground } from 'common/styles/colors';
import { getCanUserEditRegistrationForm, getCanUserEditTicketsTab } from 'common/store/permissions';
import {
    getCanUserUpdateLibraryFormsUserPermission,
    getCanUserDeleteLibraryFormsUserPermission,
    getCanUserCreateLibraryFormsUserPermission,
} from 'common/store/userPermissions/selectors';

import { fetchPermissions } from 'common/store/userPermissions';
import { setFormSessions } from 'common/compounds/FormRenderer/actions';

import DragDropContextWrapper from './DragDropContextWrapper';
import FormBuilderPreviewPane from './FormBuilderPreviewPane';
import FormEditor from './FormEditor';
import FormTemplateLibraryModal from '../TemplateLibrary/FormTemplateLibraryModal';

import { getFormId, getFormIsLoading, getMounted } from '../../selectors';
import {
    mountFormBuilder,
    loadBootstrapData,
    loadFormDataByByOwnerAndOwnerId,
    loadFormDataByFormId,
    fetchTicketSaleForm,
    setActiveSidebarTab,
} from '../../actions';
import { createFormNodeFromTemplate } from '../../actions/nodeActions';
import { OWNER_TICKET_SALE_TYPE } from '../../constants/ownerTypes';
import { QUESTIONS } from '../../constants/sidebarTabValues';

import FreemiumModals from './FreemiumModals';
import { mockSessions } from './mockSessions';

import './styles.styl';

export const FormBuilderContext = React.createContext();

export const BASE_CLASS_NAME = 'FormBuilder';
const TICKET_DISCRIMINATOR = 'tickets';
const RSVP_DISCRIMINATOR = 'collect';
const USER_PERMISSIONS = [
    'edit_registration_form_touchpoint',
    'create_registration_form_touchpoint',
    'delete_registration_form_touchpoint',
    'edit_tickets_tab',
    'create_tickets_tab',
    'delete_tickets_tab',
];

const LOG = new Logger('FormBuilder');

export const getFormBuilderCanUserEdit = ({
    isUsingRoleBasedPermissions,
    isTicketedEvent,
    isRsvpEvent,
    isFormLibrary,
    canUserEditTicketsTab,
    canUserEditRegistrationForm,
    canUserEditLibraryForms,
    canUserAddLibraryForms,
    canUserDeleteLibraryForms,
    canUserEditRsvpFormOverride,
}) => {
    const canUserEditRsvpForm =
        typeof canUserEditRsvpFormOverride === 'boolean' ? canUserEditRsvpFormOverride : canUserEditRegistrationForm;

    return cond([
        [() => !isUsingRoleBasedPermissions, () => true],
        [() => isTicketedEvent, () => canUserEditTicketsTab],
        [() => isRsvpEvent, () => canUserEditRsvpForm],
        [() => isFormLibrary, () => canUserEditLibraryForms || canUserAddLibraryForms || canUserDeleteLibraryForms],
        [
            () => true,
            () => {
                LOG.error('[Form builder] Registration method is unknown');
                return false;
            },
        ],
    ])();
};

export const getFormBuilderViewOnly = ({ discriminator, viewOnly, canUserEditRsvpFormOverride, canUserEditForm }) =>
    viewOnly ||
    (discriminator === REGISTRATION_METHOD_RSVP && typeof canUserEditRsvpFormOverride === 'boolean'
        ? !canUserEditForm
        : false);

class FormBuilder extends Component {
    static propTypes = {
        /** Pass to load form associated with this id. */
        formId: PropTypes.string,
        canUserEditRsvpFormOverride: PropTypes.bool,
        viewOnly: PropTypes.bool,
        toolbarTitle: PropTypes.string,
        /** Pass to show the left panel with FormRenderer. */
        showPreview: PropTypes.bool.isRequired,
        /** Pass to enable form styling tab. */
        stylingEnabled: PropTypes.bool.isRequired,
        closeForm: PropTypes.func,
        isLoading: PropTypes.bool,
        mounted: PropTypes.bool.isRequired,
        loadBootstrapData: PropTypes.func.isRequired,
        mountFormBuilder: PropTypes.func.isRequired,
        createFormNodeFromTemplate: PropTypes.func.isRequired,
        loadFormDataByByOwnerAndOwnerId: PropTypes.func.isRequired,
        loadFormDataByFormId: PropTypes.func.isRequired,
        fetchTicketSaleForm: PropTypes.func.isRequired,
        setActiveSidebarTab: PropTypes.func.isRequired,
        isBackButtonVisible: PropTypes.bool,
        isRoutingContext: PropTypes.bool,
        backPath: PropTypes.string,
        discriminator: PropTypes.oneOf([REGISTRATION_METHOD_RSVP, REGISTRATION_METHOD_TICKETS]),
        ownerType: PropTypes.string,
        ownerId: PropTypes.string,
        permissions: PropTypes.shape({
            canUserEditForm: PropTypes.bool,
            isUsingRoleBasedPermissions: PropTypes.bool,
        }).isRequired,
        fetchPermissions: PropTypes.func.isRequired,
        isFormLibraryWorkspace: PropTypes.bool,
        isTicketEditor: PropTypes.bool,
        openSaveModal: PropTypes.func,
        flags: PropTypes.object.isRequired,
        setFormSessions: PropTypes.func.isRequired,
        useEventFormEndpoints: PropTypes.bool,
    };

    static defaultProps = {
        formId: null,
        canUserEditRsvpFormOverride: undefined,
        viewOnly: false,
        isLoading: true,
        closeForm: null,
        isBackButtonVisible: false,
        isRoutingContext: false,
        backPath: '/',
        discriminator: REGISTRATION_METHOD_RSVP,
        ownerType: null,
        ownerId: null,
        toolbarTitle: '',
        isFormLibraryWorkspace: false,
        isTicketEditor: false,
        openSaveModal: null,
        useEventFormEndpoints: false,
    };

    constructor(props) {
        super(props);

        // Informative error when developing.
        if (__DEVELOPMENT__) {
            if ((!this.props.ownerType && !this.props.ownerId) || this.props.formId) {
                LOG.error('FormBuilder needs ownerType and ownerId or formId passed.');
            }
        }
    }

    getClassNames() {
        return [BASE_CLASS_NAME, !this.props.showPreview && `${BASE_CLASS_NAME}--tab-only`].filter((v) => v).join(' ');
    }

    componentDidMount() {
        const { ownerType, ownerId, discriminator, isFormLibraryWorkspace, isTicketEditor, useEventFormEndpoints } =
            this.props;
        this.props.mountFormBuilder({ useEventFormEndpoints });
        this.props.loadBootstrapData();
        this.props.setFormSessions(mockSessions);

        if (!isFormLibraryWorkspace && !isTicketEditor) {
            this.props.fetchPermissions(USER_PERMISSIONS);
            if (ownerType && ownerId) {
                if (ownerType === OWNER_TICKET_SALE_TYPE) {
                    this.props.setActiveSidebarTab(QUESTIONS);
                }
                // ownerId can also be -1 that means that
                // a new ticket is created, thus a new form needs to be created as well
                if (ownerId < 0 && ownerType === OWNER_TICKET_SALE_TYPE) {
                    this.props.fetchTicketSaleForm();
                } else {
                    this.props.loadFormDataByByOwnerAndOwnerId(ownerType, ownerId, discriminator);
                }
            } else if (this.props.formId) {
                this.props.loadFormDataByFormId(this.props.formId, {
                    firstTimeOnly: false,
                });
            }
        }
    }

    render() {
        const {
            closeForm,
            showPreview,
            stylingEnabled,
            isLoading,
            isBackButtonVisible,
            backPath,
            isRoutingContext,
            viewOnly,
            canUserEditRsvpFormOverride,
            discriminator,
            toolbarTitle,
            permissions,
            isFormLibraryWorkspace,
            openSaveModal,
        } = this.props;
        const effectiveViewOnly = getFormBuilderViewOnly({
            discriminator,
            viewOnly,
            canUserEditRsvpFormOverride,
            canUserEditForm: permissions.canUserEditForm,
        });

        if (!this.props.mounted) {
            return null;
        }

        return (
            <FormBuilderContext.Provider
                value={{
                    isBackButtonVisible,
                    backPath,
                    isRoutingContext,
                    viewOnly: effectiveViewOnly,
                    toolbarTitle,
                    permissions,
                }}
            >
                <DragDropContextWrapper>
                    <SuiContainer
                        className={this.getClassNames()}
                        grow
                        variation="flex-horizontal"
                        style={{ alignItems: 'flex-start' }}
                    >
                        {showPreview && !isLoading && (
                            <FormBuilderPreviewPane
                                className={`${BASE_CLASS_NAME}__left-pane`}
                                closeForm={closeForm}
                                viewOnly={effectiveViewOnly}
                                isFormLibraryWorkspace={isFormLibraryWorkspace}
                                openSaveModal={openSaveModal}
                                isUsingRoleBasedPermissions={permissions.isUsingRoleBasedPermissions}
                            />
                        )}
                        {!isLoading && (
                            <FormEditor className={`${BASE_CLASS_NAME}__right-pane`} stylingEnabled={stylingEnabled} />
                        )}
                        <CubeLoaderOverlay
                            loading={isLoading}
                            backgroundColor={colorAppBackground}
                            size={FULLSCREEN_SIZE}
                            useAlternate
                        />
                        <FormTemplateLibraryModal
                            createNodeFromTemplate={this.props.createFormNodeFromTemplate}
                            hasAccessToFormProtectedFields={this.props.flags.hasAccessToFormProtectedFields}
                        />
                    </SuiContainer>
                </DragDropContextWrapper>
                <FreemiumModals />
            </FormBuilderContext.Provider>
        );
    }
}

const mapStateToProps = (state, props) => {
    // Use id in form builder namespace or passed in through props (for first time, before fetching)
    const formId = getFormId(state) || props.formId;
    // Retrieve eventId and discriminator(rsvpMethod) from the store entities.
    const eventId = getEventId(state);
    const discriminator = getEventRsvpMethod(state, eventId);
    const isRsvpEvent = getEventRsvpMethod(state, eventId) === RSVP_DISCRIMINATOR;
    const isTicketedEvent = getEventRsvpMethod(state, eventId) === TICKET_DISCRIMINATOR || !!getCurrentTicketId(state);
    const isFormLibrary = props.isFormLibraryWorkspace;
    // If no form id, we're on an event context. That means it's loading until it fetches form.
    const isLoading = !formId || getFormIsLoading(state, formId) || Boolean(props.isFormLibraryFormLoading);

    const mounted = getMounted(state);
    const canUserEditRegistrationForm = getCanUserEditRegistrationForm(state);
    const canUserEditLibraryForms = getCanUserUpdateLibraryFormsUserPermission(state);
    const canUserAddLibraryForms = getCanUserCreateLibraryFormsUserPermission(state);
    const canUserDeleteLibraryForms = getCanUserDeleteLibraryFormsUserPermission(state);
    // for form, role based permissions are added in store as well as in bootstrap via tickets_controller
    // we want to default to value from store and if false, want to check value in boostrap
    // when rendered inside tickets, form does not have access to all namespaces so can return false results
    const canUserEditTicketsTabStore = getCanUserEditTicketsTab(state);
    const isUserUsingRoleBasedPermissionsStore = getIsUserUsingRoleBasedPermissions(state);
    const canUserEditTicketsTabWindow = get(window, 'splash.server.constants.permissions.edit_tickets_tab', false);
    const isUserUsingRoleBasedPermissionsWindow = get(
        window,
        'splash.server.constants.permissions.is_using_role_based_permissions',
        false,
    );
    const canUserEditTicketsTab = canUserEditTicketsTabStore || canUserEditTicketsTabWindow;
    const isUsingRoleBasedPermissions = isUserUsingRoleBasedPermissionsStore || isUserUsingRoleBasedPermissionsWindow;
    const canUserEdit = getFormBuilderCanUserEdit({
        isUsingRoleBasedPermissions,
        isTicketedEvent,
        isRsvpEvent,
        isFormLibrary,
        canUserEditTicketsTab,
        canUserEditRegistrationForm,
        canUserEditLibraryForms,
        canUserAddLibraryForms,
        canUserDeleteLibraryForms,
        canUserEditRsvpFormOverride: props.canUserEditRsvpFormOverride,
    });
    return {
        isLoading,
        mounted,
        discriminator,
        permissions: {
            isUsingRoleBasedPermissions,
            canUserEditForm: canUserEdit,
        },
    };
};

const mapDispatchToProps = {
    mountFormBuilder,
    loadBootstrapData,
    createFormNodeFromTemplate,
    loadFormDataByByOwnerAndOwnerId,
    loadFormDataByFormId,
    fetchTicketSaleForm,
    setActiveSidebarTab,
    fetchPermissions,
    setFormSessions,
};

export default withFlags(connect(mapStateToProps, mapDispatchToProps)(FormBuilder));
